import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, HelpCircle, Megaphone } from 'lucide-react';
import Button from '../../common/Button/Button';
import Card from '../../common/Card/Card';
import RangeSlider from '../../common/RangeSlider/RangeSlider';
import TriStateToggle from '../../common/TriStateToggle/TriStateToggle';
import FilterSection from '../../common/FilterSection/FilterSection';
import Toggle from '../../common/Toggle/Toggle';
import { ResultsFilter as FilterType, SearchStatistics } from '../../../types/results.types';

const GOOGLE_ADS_FRESHNESS_DAYS = 90;

const stripSmartFilterParams = (filters: FilterType): FilterType => {
  const safeFilters = { ...filters };
  delete safeFilters.strongMatchesOnly;
  delete safeFilters.matchedOffering;
  return safeFilters;
};

interface ResultsFilterProps {
  currentFilters: FilterType;
  onApplyFilters: (filters: FilterType) => void;
  availableIndustries: string[];
  disabled?: boolean;
  resultsCount?: number;
  statistics?: SearchStatistics | null;
  hasSmartFilters?: boolean;
}

const ResultsFilter: React.FC<ResultsFilterProps> = ({
  currentFilters,
  onApplyFilters,
  availableIndustries,
  disabled = false,
  resultsCount,
  statistics,
  hasSmartFilters = false,
}) => {
  const [filters, setFilters] = useState<FilterType>(currentFilters || {});
  const [industrySearch, setIndustrySearch] = useState('');
  const [isIndustryOpen, setIsIndustryOpen] = useState(false);
  const [isAdsHelpOpen, setIsAdsHelpOpen] = useState(false);
  
  const minRating = statistics?.min_rating ?? 0;
  const maxRating = statistics?.max_rating ?? 5;
  const minReviews = statistics?.min_reviews ?? 0;
  const maxReviews = statistics?.max_reviews ?? 1000;
  
  useEffect(() => {
    if (hasSmartFilters) {
      setFilters(currentFilters || {});
      return;
    }
    setFilters(stripSmartFilterParams(currentFilters || {}));
  }, [currentFilters, hasSmartFilters]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value || undefined,
    }));
  };

  const handleRatingChange = (min: number, max: number) => {
    setFilters(prev => ({
      ...prev,
      minRating: min === minRating ? undefined : min,
      maxRating: max === maxRating ? undefined : max,
    }));
  };

  const handleReviewsChange = (min: number, max: number) => {
    setFilters(prev => ({
      ...prev,
      minReviews: min === minReviews ? undefined : min,
      maxReviews: max === maxReviews ? undefined : max,
    }));
  };
  
  const handleIndustryChange = (industry: string, checked: boolean) => {
    setFilters(prev => {
      const currentIndustries = prev.industry ? prev.industry.split(',') : [];
      let newIndustries: string[];
      
      if (checked) {
        newIndustries = [...currentIndustries, industry];
      } else {
        newIndustries = currentIndustries.filter(i => i !== industry);
      }
      
      return {
        ...prev,
        industry: newIndustries.length > 0 ? newIndustries.join(',') : undefined,
      };
    });
  };

  type ContactToggleKey = 'hasWebsite' | 'hasPhone' | 'hasBookVisit' | 'hasDecisionMakers' | 'hasEmail';

  const handleContactToggleChange = (name: ContactToggleKey, value: boolean | null) => {
    setFilters(prev => ({
      ...prev,
      [name]: value === null ? undefined : value,
    }));
  };

  const handleEmailVerificationStatusChange = (status: string, checked: boolean) => {
    setFilters(prev => {
      const currentStatuses = prev.emailVerificationStatus || [];
      let newStatuses: string[];
      
      if (checked) {
        newStatuses = [...currentStatuses, status];
      } else {
        newStatuses = currentStatuses.filter(s => s !== status);
      }
      
      return {
        ...prev,
        emailVerificationStatus: newStatuses.length > 0 ? newStatuses : undefined,
      };
    });
  };

  const handleStrongMatchesChange = (checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      strongMatchesOnly: checked ? true : undefined,
    }));
  };

  const handleGoogleAdsSignalChange = (value: boolean | null) => {
    setFilters(prev => ({
      ...prev,
      googleAdsSignal: value === true ? 'detected_recently' : value === false ? 'no_ad_tracking' : undefined,
    }));
  };

  const googleAdsToggleValue: boolean | null =
    filters.googleAdsSignal === 'detected_recently'
      ? true
      : filters.googleAdsSignal === 'no_ad_tracking'
        ? false
        : null;

  const isEmailVerificationStatusSelected = (status: string) => {
    if (!filters.emailVerificationStatus) return false;
    return filters.emailVerificationStatus.includes(status);
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (hasSmartFilters) {
      onApplyFilters(filters);
      return;
    }
    onApplyFilters(stripSmartFilterParams(filters));
  };
  
  const handleReset = () => {
    setFilters({});
    setIndustrySearch('');
    setIsIndustryOpen(false);
    setIsAdsHelpOpen(false);
    onApplyFilters({});
  };
  
  const isIndustrySelected = (industry: string) => {
    if (!filters.industry) return false;
    return filters.industry.split(',').includes(industry);
  };

  const filteredIndustries = availableIndustries.filter(industry =>
    industry.toLowerCase().includes(industrySearch.toLowerCase())
  );

  const selectedIndustries = filters.industry ? filters.industry.split(',') : [];
  const industrySummary = selectedIndustries.length > 0 ? `${selectedIndustries.length} selected` : 'All industries';

  const emailStatusOptions = [
    { value: 'safe', label: 'Verified' },
    { value: 'likely', label: 'Likely' },
    { value: 'unconfirmed', label: 'Unconfirmed' },
  ];

  const contactToggleConfig: { key: ContactToggleKey; label: string }[] = [
    { key: 'hasWebsite', label: 'Website' },
    { key: 'hasPhone', label: 'Phone Number' },
    { key: 'hasBookVisit', label: 'Booking Information' },
    { key: 'hasDecisionMakers', label: 'Decision Makers' },
    { key: 'hasEmail', label: 'Email' },
  ];

  return (
    <Card className="relative flex h-[calc(100vh-120px)] w-full flex-col border-r border-gray-100 bg-white shadow-sm">
      {disabled && (
        <div className="mb-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
          <p className="text-sm text-yellow-800">
            🔒 Filtering is disabled when results are locked. Please unlock results to use filters.
          </p>
        </div>
      )}

      <div className="pb-3">
        <h3 className="text-base font-bold text-gray-900">Filter Results</h3>
        {typeof resultsCount !== 'undefined' && (
          <p className="mt-1 text-xs text-gray-500">{resultsCount} rows available</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-1 flex-col">
        <div className="flex-1 space-y-4 overflow-y-auto pr-1 pt-2">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-900" htmlFor="searchTerm">Search by Name</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
              <input
                id="searchTerm"
                name="searchTerm"
                type="text"
                placeholder="Business name..."
                value={filters.searchTerm || ''}
                onChange={handleChange}
                disabled={disabled}
                className="w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:cursor-not-allowed disabled:bg-gray-100"
              />
            </div>
          </div>

          {availableIndustries.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-gray-900">Main & Sub Industries</label>
                <span className="text-xs text-gray-500">{industrySummary}</span>
              </div>

              <button
                type="button"
                onClick={() => !disabled && setIsIndustryOpen(prev => !prev)}
                disabled={disabled}
                className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  disabled
                    ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                    : 'border-gray-200 bg-white text-gray-800 hover:border-primary/40 hover:bg-primary/5'
                }`}
              >
                <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                  {selectedIndustries.length ? (
                    <div className="flex flex-wrap items-center gap-1 overflow-hidden">
                      {selectedIndustries.slice(0, 3).map((name) => (
                        <span
                          key={name}
                          className="max-w-[9rem] truncate rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 text-[11px] text-primary"
                          title={name}
                        >
                          {name}
                        </span>
                      ))}
                      {selectedIndustries.length > 3 && (
                        <span className="text-xs text-gray-500">+{selectedIndustries.length - 3}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-500">Select industries</span>
                  )}
                </div>
                {isIndustryOpen ? (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
              </button>

              {isIndustryOpen && (
                <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 shadow-inner">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search industries..."
                      value={industrySearch}
                      onChange={(e) => setIndustrySearch(e.target.value)}
                      disabled={disabled}
                      className="w-full rounded-md border border-gray-200 bg-white pl-8 pr-3 py-2 text-xs text-gray-800 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                  </div>

                  <div className="max-h-44 space-y-1 overflow-y-auto rounded-md border border-gray-100 bg-white p-2">
                    {filteredIndustries.length > 0 ? (
                      filteredIndustries.map(industry => (
                        <label
                          key={industry}
                          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-gray-700 hover:bg-primary/5"
                        >
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary/50 disabled:cursor-not-allowed"
                            checked={isIndustrySelected(industry)}
                            onChange={(e) => handleIndustryChange(industry, e.target.checked)}
                            disabled={disabled}
                          />
                          <span className="truncate">{industry}</span>
                        </label>
                      ))
                    ) : (
                      <p className="py-2 text-center text-xs text-gray-500">No industries found</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
            <RangeSlider
              label="Google ratings"
              min={minRating}
              max={maxRating}
              step={0.1}
              minValue={filters.minRating ?? minRating}
              maxValue={filters.maxRating ?? maxRating}
              onChange={handleRatingChange}
              disabled={disabled}
            />

            <RangeSlider
              label="Number of Google reviews"
              min={minReviews}
              max={maxReviews}
              step={1}
              minValue={filters.minReviews ?? minReviews}
              maxValue={filters.maxReviews ?? maxReviews}
              onChange={handleReviewsChange}
              disabled={disabled}
            />
          </div>

          <FilterSection title="Contact & Booking" defaultExpanded>
            {contactToggleConfig.map(item => (
              <div key={item.key} className="flex items-center justify-between py-1">
                <span className="text-sm font-medium text-gray-700">{item.label}</span>
                <TriStateToggle
                  value={filters[item.key] ?? null}
                  onChange={(value) => handleContactToggleChange(item.key, value)}
                  disabled={disabled}
                />
              </div>
            ))}
          </FilterSection>

          <FilterSection title="Advertising signals" defaultExpanded>
            <div className="rounded-xl border border-[#E7E5E1] bg-[#FAFAF9] p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Megaphone className="h-4 w-4 text-[#2B6CB0]" aria-hidden="true" />
                  <span className="text-sm font-semibold text-gray-800">Google Ads</span>
                  <span className="rounded border border-[#8A3033]/20 bg-[#8A3033]/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#8A3033]">
                    Beta
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAdsHelpOpen(open => !open)}
                  disabled={disabled}
                  aria-expanded={isAdsHelpOpen}
                  aria-controls="google-ads-filter-help"
                  aria-label="About the Google Ads website signal"
                  className="rounded-full p-1 text-gray-400 transition hover:bg-white hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#2B6CB0]/40"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </div>

              <TriStateToggle
                value={googleAdsToggleValue}
                onChange={handleGoogleAdsSignalChange}
                disabled={disabled}
              />

              <p className="mt-2 text-[11px] leading-4 text-gray-500">
                We found Google Ads tracking code on their site. This doesn&apos;t guarantee a campaign is running right now.
              </p>
              <p className="mt-1 text-[11px] leading-4 text-gray-400">
                Detected within the last {GOOGLE_ADS_FRESHNESS_DAYS} days.
              </p>
              {isAdsHelpOpen && (
                <div
                  id="google-ads-filter-help"
                  className="mt-2 rounded-lg bg-[#2D3142] p-3 text-[11px] leading-5 text-gray-200"
                >
                  We look for Google Ads-specific tags and conversion requests on website pages checked in the last {GOOGLE_ADS_FRESHNESS_DAYS} days. A detected signal may come from a live, paused, historical, or remarketing setup.
                </div>
              )}
            </div>
          </FilterSection>

          {hasSmartFilters && (
            <FilterSection title="Smart Filters" defaultExpanded>
              <Toggle
                id="strongMatchesOnly"
                label="Matched businesses"
                checked={filters.strongMatchesOnly === true}
                onChange={handleStrongMatchesChange}
                disabled={disabled}
              />
            </FilterSection>
          )}

          <FilterSection title="Email Verification Status" defaultExpanded>
            <div className="flex flex-wrap gap-2">
              {emailStatusOptions.map(option => {
                const isActive = isEmailVerificationStatusSelected(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleEmailVerificationStatusChange(option.value, !isActive)}
                    disabled={disabled}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                      isActive
                        ? 'border-primary bg-primary text-white shadow-sm'
                        : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-primary/50 hover:text-gray-900'
                    } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </FilterSection>
        </div>

        <div className="mt-3 border-t border-gray-200 bg-white pt-3">
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="text"
              onClick={handleReset}
              size="sm"
              className="w-28 justify-center text-sm"
              disabled={disabled}
            >
              Reset
            </Button>
            <Button
              type="submit"
              size="sm"
              className="flex-1 justify-center text-sm"
              disabled={disabled}
            >
              {resultsCount !== undefined ? `Apply Filters (${resultsCount})` : 'Apply Filters'}
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default ResultsFilter;
